@use "../../styles/mixins/component-wrapper" as wrapper;

@include wrapper.layered-component {
  button {
    border: none; /* reset browser default */
    border-radius: var(--semantic-border-radius-m);
    font-family: var(--semantic-font-family-utility);
    font-size: var(--semantic-font-size-utility-s);
    font-style: normal;
    font-weight: var(--semantic-font-weight-utility-attention);
    line-height: var(--semantic-line-height-utility-s);
    letter-spacing: var(--semantic-letter-spacing-utility-s);
    cursor: pointer;

    &[disabled] {
      cursor: not-allowed;
    }

    &.inverse {
      /*
        Overwrite existing and already assigned tokens with the inverted style tokens.
      */
      --semantic-color-action-surface-attention-resting: var(--semantic-color-action-surface-attention-inverse-resting);
      --semantic-color-action-surface-attention-hover: var(--semantic-color-action-surface-attention-inverse-hover);
      --semantic-color-action-surface-attention-active: var(--semantic-color-action-surface-attention-inverse-active);
      --semantic-color-action-surface-attention-disabled: var(
        --semantic-color-action-surface-attention-inverse-disabled
      );
      --semantic-color-on-action-primary-resting: var(--semantic-color-on-action-primary-inverse-resting);
      --semantic-color-on-action-primary-disabled: var(--semantic-color-on-action-primary-inverse-disabled);
      --semantic-color-action-primary-resting: var(--semantic-color-action-primary-inverse-resting);
      --semantic-color-action-primary-hover: var(--semantic-color-action-primary-inverse-hover);
      --semantic-color-action-primary-active: var(--semantic-color-action-primary-inverse-active);
      --semantic-color-action-primary-disabled: var(--semantic-color-action-primary-inverse-disabled);
      --semantic-color-action-secondary-resting: var(--semantic-color-action-secondary-inverse-resting);
      --semantic-color-action-secondary-hover: var(--semantic-color-action-secondary-inverse-hover);
      --semantic-color-action-secondary-active: var(--semantic-color-action-secondary-inverse-active);
      --semantic-color-action-secondary-disabled: var(--semantic-color-action-secondary-inverse-disabled);
      --semantic-color-border-primary-resting: var(--semantic-color-border-primary-inverse-resting);
      --semantic-color-border-primary-disabled: var(--semantic-color-border-primary-inverse-disabled);
      --semantic-color-on-signal-attention-resting: var(--semantic-color-on-signal-attention-inverse-resting);
      --semantic-color-on-signal-attention-disabled: var(--semantic-color-on-signal-attention-inverse-disabled);
    }

    /* TYPES */
    &.contained {
      background: var(--semantic-color-action-surface-attention-resting);
      padding: var(--button-contained-m-inset-vertical) var(--button-contained-m-inset-horizontal);

      &.s {
        padding: var(--button-contained-s-inset-vertical) var(--button-contained-s-inset-horizontal);
      }

      /* VARIATIONS */
      &.primary {
        color: var(--semantic-color-on-action-primary-resting);
        fill: var(--semantic-color-on-action-primary-resting);
        background: var(--semantic-color-action-surface-attention-resting);

        &:hover {
          background: var(--semantic-color-action-surface-attention-hover);
        }

        &:active {
          background: var(--semantic-color-action-surface-attention-active);
        }

        &[disabled] {
          color: var(--semantic-color-on-action-primary-disabled);
          background: var(--semantic-color-action-surface-attention-disabled);
        }
      }

      &.secondary {
        color: var(--semantic-color-action-primary-resting);
        fill: var(--semantic-color-action-primary-resting);
        border: var(--semantic-border-width-action) solid var(--semantic-color-action-surface-attention-resting);
        background-color: var(--semantic-color-surface-none);
        padding: var(--button-contained-m-inset-vertical) var(--button-contained-m-inset-horizontal);

        &.s {
          padding: var(--button-contained-s-inset-vertical) var(--button-contained-s-inset-horizontal);
        }

        &:hover {
          color: var(--semantic-color-on-action-primary-resting);
          fill: var(--semantic-color-on-action-primary-resting);
          background-color: var(--semantic-color-action-surface-attention-hover);
        }

        &:active {
          color: var(--semantic-color-on-action-primary-resting);
          fill: var(--semantic-color-on-action-primary-resting);
          background-color: var(--semantic-color-action-surface-attention-active);
        }

        &[disabled] {
          color: var(--semantic-color-on-action-primary-disabled);
          fill: var(--semantic-color-on-action-primary-disabled);
          background-color: var(--semantic-color-surface-none);
          border: var(--semantic-border-width-action) solid var(--semantic-color-action-surface-attention-disabled);
        }
      }

      &.tertiary {
        color: var(--semantic-color-action-primary-resting);
        fill: var(--semantic-color-action-primary-resting);
        background-color: var(--semantic-color-surface-none);
        text-decoration: underline;

        &:hover {
          color: var(--semantic-color-on-action-primary-resting);
          fill: var(--semantic-color-on-action-primary-resting);
          background-color: var(--semantic-color-action-surface-attention-hover);
          text-decoration: none;
        }

        &:active {
          color: var(--semantic-color-on-action-primary-resting);
          fill: var(--semantic-color-on-action-primary-resting);
          background-color: var(--semantic-color-action-surface-attention-active);
          text-decoration: none;
        }

        &[disabled] {
          color: var(--semantic-color-action-primary-disabled);
          fill: var(--semantic-color-action-primary-disabled);
          background-color: var(--semantic-color-surface-none);
          text-decoration: underline;
        }
      }

      &.critical {
        color: var(--semantic-color-on-signal-attention-resting);
        background: var(--semantic-color-signal-attention-critical-resting);

        &:hover {
          background: var(--semantic-color-signal-attention-critical-hover);
        }

        &:active {
          background: var(--semantic-color-signal-attention-critical-active);
        }

        &[disabled] {
          color: var(--semantic-color-on-signal-attention-disabled);
          background: var(--semantic-color-signal-attention-disabled);
        }
      }
    }

    &.plain {
      border-radius: var(--semantic-border-radius-s);
      font-size: var(--semantic-font-size-utility-m);
      line-height: var(--semantic-line-height-utility-m);
      letter-spacing: var(--semantic-letter-spacing-utility-m);
      text-decoration-line: underline;
      text-underline-position: from-font;
      background-color: var(--semantic-color-surface-none);

      &.s {
        font-size: var(--semantic-font-size-utility-s);
        line-height: var(--semantic-line-height-utility-s);
      }

      /* VARIATIONS */
      &.primary {
        color: var(--semantic-color-action-primary-resting);

        &:hover {
          color: var(--semantic-color-action-primary-hover);
        }

        &:active {
          color: var(--semantic-color-action-primary-active);
        }

        &[disabled] {
          color: var(--semantic-color-action-primary-disabled);
        }
      }

      &.secondary {
        color: var(--semantic-color-action-secondary-resting);

        &:hover {
          color: var(--semantic-color-action-secondary-hover);
        }

        &:active {
          color: var(--semantic-color-action-secondary-active);
        }

        &[disabled] {
          color: var(--semantic-color-action-secondary-disabled);
        }
      }

      &.critical {
        color: var(--semantic-color-signal-attention-critical-resting);

        &:hover {
          color: var(--semantic-color-signal-attention-critical-hover);
        }

        &:active {
          color: var(--semantic-color-signal-attention-critical-active);
        }

        &[disabled] {
          color: var(--semantic-color-signal-attention-disabled);
        }
      }
    }

    /* FOCUS STATES */
    &:focus {
      outline: var(--semantic-border-width-focus) solid var(--semantic-color-border-focus);
      outline-offset: var(--semantic-spacing-focus-offset);
    }

    /* FULL-WIDTH */
    &.full-width {
      width: -webkit-fill-available;
      width: -moz-available;
      width: fill-available;
      width: 100%;
    }

    /* ICON AND INDICATOR VARIATIONS */
    .btn-count-indicator {
      position: absolute;
      left: var(--button-contained-m-icon-indicator-count-offset-start);
      bottom: var(--button-contained-m-icon-indicator-count-offset-bottom);
    }

    .btn-indicator {
      position: absolute;
      left: var(--button-contained-m-icon-indicator-countless-offset-start);
      bottom: var(--button-contained-m-icon-indicator-countless-offset-bottom);
    }

    &.icon-before {
      .btn-icon {
        display: inline-block;
        position: relative;
        line-height: 0;
        margin-right: var(--semantic-spacing-content-s-gap-contained);
        vertical-align: middle;

        svg {
          display: inline-block;
          margin: 0;
          width: var(--semantic-size-content-s);
          height: var(--semantic-size-content-s);
        }
      }
    }

    &.icon-after {
      display: flex;

      .btn-icon {
        order: 1;
        position: relative;
        line-height: 0;
        margin-left: var(--semantic-spacing-content-s-gap-contained);
        vertical-align: middle;

        svg {
          display: inline-block;
          margin: 0;
          width: var(--semantic-size-content-s);
          height: var(--semantic-size-content-s);
        }
      }
    }

    &.icon-only {
      position: relative;
      padding: var(--button-contained-m-icon-inset-all);
      line-height: 0;
      margin: 0;

      .btn-icon {
        display: inline-block;
        position: relative;

        svg {
          display: inline-block;
          margin: 0;
          width: var(--semantic-size-content-s);
          height: var(--semantic-size-content-s);
        }
      }
    }
  }
}
